import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';

const CsvUploadModal = ({ onFileUpload, isProcessing, error, validationResults }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const modalRef = useRef(null);

  // Watch for changes in validation results to auto-close modal
  useEffect(() => {
    if (validationResults?.length > 0 && !isProcessing) {
      setIsOpen(false);
    }
  }, [validationResults, isProcessing]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type === "text/csv") {
      // Create a synthetic event object that matches what the parent component expects
      onFileUpload({
        target: {
          files: [file]
        }
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === "text/csv") {
      onFileUpload(e);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#745d96] transition-colors"
      >
        <Upload className="h-4 w-4 mr-2" />
        Upload CSV
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !isProcessing && setIsOpen(false)}
            />

            {/* Modal */}
            <div
              ref={modalRef}
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              {/* Close button */}
              <div className='flex justify-between items-center  '>


                {/* Modal header */}
                <div className="">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Upload CSV File
                  </h3>
                </div>

                <button
                  onClick={() => !isProcessing && setIsOpen(false)}
                  disabled={isProcessing}
                  className={`  text-gray-400 ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-500'
                    }`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Upload area */}
              <div
                className={`mt-4 ${dragActive ? "border-[#745d96] bg-purple-50" : "border-gray-200"
                  } border-2 border-dashed rounded-lg p-12 transition-colors`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {isProcessing ? (
                  <div className="text-center space-y-3">
                    <Loader2 className="h-12 w-12 mx-auto text-purple-600 animate-spin" />
                    <p className="text-sm text-gray-500">Processing CSV file...</p>
                    <p className="text-xs text-gray-400">Please wait while we analyze your data</p>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="space-y-2">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex justify-center text-sm text-gray-600">
                        <label className="relative cursor-pointer rounded-md font-medium text-purple-600 hover:text-[#745d96] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#745d96]">
                          <span>Upload a file</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept=".csv"
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">CSV files only</p>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Error Processing File
                      </h3>
                      <div className="mt-1">
                        {error.includes("Missing required columns") ? (
                          <>
                            <p className="text-sm text-red-700">
                              The following required columns are missing:
                            </p>
                            <ul className="mt-2 list-disc list-inside text-sm text-red-700 space-y-1">
                              {error
                                .replace("Error processing file: Missing required columns: ", "")
                                .split(", ")
                                .map((column) => (
                                  <li key={column} className="ml-2">{column}</li>
                                ))}
                            </ul>
                            <p className="mt-2 text-sm text-red-700">
                              Please ensure your CSV file contains all required columns.
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-red-700">{error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-red-200 bg-red-50 px-4 py-3">
                    <div className="flex items-center space-x-2 text-sm text-red-700">
                      <AlertTriangle className="h-4 w-4" />
                      <p>Upload a new file to try again</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CsvUploadModal;
